/**
 * @module components/GuidedCheckout
 * @description Q1314 Tier 2 "Guided giving checkout: one question per screen,
 * progress indicator, keyboard-friendly, saves progress".
 *
 * Steps reuse DonorFields groups (no duplicated field code). Progress (step +
 * donor details) is saved to this visit's sessionStorage so a reload resumes.
 */
import { useEffect, useRef, useState, type FormEvent, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { validateDonor, type DonorErrors } from "../domain/donor";
import type { CartLine, DonorInfo } from "../domain/types";
import { formatUsd } from "../lib/format";
import { readJson, STORAGE_KEYS, writeJson } from "../lib/storage";
import { AmountPicker } from "./AmountPicker";
import { DonorFields, type DonorFieldGroup } from "./DonorFields";
import { Icon } from "./Icons";
import "./GuidedCheckout.css";

/** One guided step: a question and the donor fields it covers. */
interface Step {
    id: string;
    question: string;
    hint: string;
    groups: DonorFieldGroup[];
    /** Donor keys validated before leaving this step. */
    checks: (keyof DonorInfo)[];
}

/** The steps, in order. Step 0 (amounts) is rendered specially. */
const STEPS: readonly Step[] = [
    { id: "amounts", question: "How much would you like to give?", hint: "Adjust each cause. You'll pay once.", groups: [], checks: [] },
    { id: "name", question: "Who is this gift from?", hint: "As it should appear on your receipt.", groups: ["name"], checks: ["firstName", "lastName"] },
    { id: "contact", question: "Where should we send your receipt?", hint: "AFJ emails a tax receipt for every gift.", groups: ["contact"], checks: ["email"] },
    { id: "address", question: "Where are you giving from?", hint: "Used for your receipt and card verification.", groups: ["address"], checks: ["postalCode"] },
    { id: "review", question: "Ready to give?", hint: "Check the details, then continue to Neon's secure payment.", groups: ["privacy"], checks: [] },
];

/** Persisted progress key (session only). */
const PROGRESS_KEY: string = `${STORAGE_KEYS.donor}.guided`;

/** Props for <GuidedCheckout>. */
interface GuidedCheckoutProps {
    onComplete: () => void;
    errors: DonorErrors;
    setErrors: (errors: DonorErrors) => void;
}

/** The guided checkout. */
export function GuidedCheckout({ onComplete, errors, setErrors }: GuidedCheckoutProps): JSX.Element {
    const { lines, donor, setDonor, dispatchCart, causeById, totalUsd } = useGiving();
    // Read saved progress once (lazy initializer), not on every render.
    const [saved] = useState<{ step: number; donor: DonorInfo } | null>(() => readJson<{ step: number; donor: DonorInfo } | null>("session", PROGRESS_KEY, null));
    const [step, setStep] = useState<number>(saved?.step ?? 0);
    const [lineIndex, setLineIndex] = useState<number>(0);
    const heading = useRef<HTMLHeadingElement>(null);

    // Resume saved donor details once (reload mid-checkout).
    useEffect(() => {
        if (saved !== null) setDonor(saved.donor);
    }, [saved, setDonor]);

    useEffect(() => writeJson("session", PROGRESS_KEY, { step, donor }), [step, donor]);

    // Move focus to the new question so keyboard and screen-reader users follow along.
    useEffect(() => heading.current?.focus(), [step]);

    const current: Step = STEPS[step];
    const pct: number = Math.round(((step + 1) / STEPS.length) * 100);

    /** Validate this step's fields, then advance or finish. */
    const next = (event?: FormEvent<HTMLFormElement>): void => {
        event?.preventDefault();
        const all: DonorErrors = validateDonor(donor);
        const stepErrors: DonorErrors = Object.fromEntries(current.checks.filter((k) => all[k]).map((k) => [k, all[k]]));
        setErrors(stepErrors);
        if (Object.keys(stepErrors).length > 0) return;
        if (step === STEPS.length - 1) {
            // Hand-off reached: clear saved progress so the next gift starts at step 1.
            writeJson("session", PROGRESS_KEY, null);
            onComplete();
            return;
        }
        setStep(step + 1);
    };

    const line: CartLine | undefined = lines[lineIndex];

    return (
        <form className="guided" onSubmit={next} noValidate>
            <div className="guided__progress" aria-hidden="true"><div className="guided__bar" style={{ width: `${pct}%` }} /></div>
            <p className="guided__count">Step {step + 1} of {STEPS.length}</p>

            <div className="guided__screen" key={current.id}>
                <h2 className="guided__question" ref={heading} tabIndex={-1}>{current.question}</h2>
                <p className="guided__hint">{current.hint}</p>

                {current.id === "amounts" && line !== undefined && (
                    <div className="guided__amounts">
                        <div className="guided__tabs" role="tablist" aria-label="Causes in your cart">
                            {lines.map((l: CartLine, i: number) => (
                                <button key={l.causeId} type="button" role="tab" aria-selected={i === lineIndex} className="chip" aria-pressed={i === lineIndex} onClick={() => setLineIndex(i)}>
                                    {causeById(l.causeId)?.partner ?? l.causeId} · {formatUsd(l.amountUsd)}
                                </button>
                            ))}
                        </div>
                        <AmountPicker label={causeById(line.causeId)?.title ?? ""} value={line.amountUsd}
                            onChange={(amountUsd: number) => dispatchCart({ type: "setAmount", causeId: line.causeId, amountUsd })} />
                        <p className="guided__total">Total today: <strong>{formatUsd(totalUsd)}</strong></p>
                    </div>
                )}

                {current.groups.length > 0 && <DonorFields donor={donor} errors={errors} onChange={setDonor} groups={current.groups} />}

                {current.id === "review" && (
                    <dl className="guided__review">
                        <div><dt>From</dt><dd>{donor.firstName} {donor.lastName}</dd></div>
                        <div><dt>Receipt to</dt><dd>{donor.email}</dd></div>
                        <div><dt>Total</dt><dd>{formatUsd(totalUsd)} for {lines.length} causes</dd></div>
                    </dl>
                )}
            </div>

            <div className="guided__nav">
                {step > 0 && <button type="button" className="btn btn--ghost btn--lg" onClick={() => setStep(step - 1)}><Icon name="chevron-left" /> Back</button>}
                <button type="submit" className="btn btn--primary btn--lg guided__next">
                    {step === STEPS.length - 1 ? <><Icon name="lock" /> Continue to secure payment</> : <>Next <Icon name="arrow-right" /></>}
                </button>
            </div>
            <p className="guided__keys">Tip: press Enter to continue.</p>
        </form>
    );
}
